import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion"
import type { FAQItem } from "@/components/seo/faq-types";

const items = [
    {
        value: "1",
        trigger: "Are these bathroom products actually commercial-grade or just retail-ranges?",
        content:
            "Yes, we have a selection of bathroom fixtures for commercial and high‑use environments, not just for one‑off residential installs. That means durable finishes, repeatable SKUs, and commercial warranties suitable for apartment complexes, hospitality, aged care, schools, and offices. Talk to our team about specifying the right range for your project."
        ,
    },
    {
        value: "2",
        trigger: "Can you supply the same fixtures consistently across a multi‑unit or multi‑site project",
        content:
            "Absolutely. We can offer spec consistency — supplying the same taps, vanities, toilets, and mixers across multiple units or locations, even when projects are staged. Contact one of our dedicated account managers to learn more about what this looks like."
        ,
    },
    {
        value: "3",
        trigger: "Do you work with builders, plumbers, architects, and procurement teams — or just end customers?",
        content:
            "We work primarily with trade and commercial customers, including builders, plumbers, quantity surveyors, developers, architects, designers, and procurement managers. You’ll deal with a dedicated commercial account manager, not a retail counter. Get in touch to discuss your project requirements."
        ,
    },
    {
        value: "4",
        trigger: "How do your prices compare to plumbing merchants or specialist suppliers?",
        content:
            "Our pricing is trade‑based and volume‑aligned, with project pricing available for multi‑unit builds and commercial fit‑outs. We focus on total value, pricing, availability, logistics, and reduced rework. Ask for a project quote or comparison."
        ,
    },
    {
        value: "5",
        trigger: "Can you deliver to site, staged, and nationwide?",
        content:
            "Yes. We offer nationwide delivery across New Zealand, including staged drops aligned to build programmes and handover timelines. Get confirmed delivery timelines with our commercial team.",
    },
    {
        value: "6",
        trigger: "Are these fixtures suitable for aged care, hospitality, and public bathrooms?",
        content:
            "They are. Our bathroom range includes products suitable for high‑traffic and regulated environments, including aged care, hospitality, education, healthcare, offices, and shared amenities. Let us know your use case and compliance needs.",
    },
    {
        value: "7",
        trigger: "What happens if we need replacements or matching items later?",
        content:
            "We work with you to prioritise ranges with long‑term availability and consistent specifications, making it easier to source replacements or add units later without mismatches. Talk to us about future‑proofing your bathroom spec.",
    },
]

interface FAQGenericProps {
    variant?: "dark" | "light" | "default";
    heading?: React.ReactNode;
    items: FAQItem[];
}

export function FAQGeneric({
    variant = "default",
    heading = "FAQs",
    items,
}: FAQGenericProps) {
    const isDark = variant === "dark";

    return (
        <div
            className={`py-8 sm:py-16 ${variant === "dark"
                    ? "bg-black text-white"
                    : variant === "default"
                        ? "bg-gradient-to-b from-gray-50 to-gray-100 text-black"
                        : "bg-white text-black"
                }`}
        >
            <div className="max-w-6xl mx-auto text-center px-4 sm:px-8">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">{heading}</h2>
                <Accordion multiple className="" defaultValue={["1"]}>
                    {items.map((item) => (
                        <AccordionItem
                            key={item.id}
                            value={item.id}
                            className={`border-b last:border-b-0 ${isDark ? "border-white/20" : "border-gray-200"
                                }`}
                        >
                            <AccordionTrigger
                                className={`text-left text-xl font-semibold group-hover:text-harvey-red transition-colors duration-300 mt-8 no-underline hover:no-underline ${
                                    isDark ? "text-white" : "text-gray-800"
                                }`}
                            >
                                {item.question}
                            </AccordionTrigger>
                            <AccordionContent
                                className={`text-left text-sm sm:text-base md:text-lg mb-6 sm:mb-8 px-4 ${
                                    isDark ? "text-gray-300" : "text-gray-600"
                                }`}
                            >
                                {item.answerNode ?? item.answerText}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </div>
    )
}
